import { z } from "zod";

import { i18n } from "@libs/i18n";
import {
  insertEventsTypeSchema,
  publicEventAttendanceStatusEnumSchema,
  publicEventStatusEnumSchema,
  publicLocationTypeEnumSchema,
  rowActivitiesTypeSchema,
  rowEventsTypeSchema,
  rowSkillsTypeSchema,
  updateEventsTypeSchema,
} from "@libs/schemas";
import { dateAsDayjs } from "@/utils/date";

import { activitySchema } from "./activity";
import { campusSchema } from "./campus";
import { enrolmentSchema } from "./enrolment";
import { lessonSchema } from "./lesson";
import { lessonRouteOptionSchema } from "./lessonRoute";
import { locationSchema } from "./location";
import { staffSchema } from "./staff";
import { studentSchema } from "./student";
import { vehicleSchema } from "./vehicle";

export const rawEventSchema = rowEventsTypeSchema
  .partial({ staff_signature: true })
  .merge(
    z.object({
      enrolments_events: z
        .array(
          z.object({
            id: z.string().uuid().optional(),
            attendance_status: publicEventAttendanceStatusEnumSchema
              .optional()
              .catch("NOT_RECORDED"),
            is_optional_attendance: z.boolean().optional().catch(false),
            student_signature: z.string().nullish(),
            student_sign_at: z.string().nullish(),
            enrolment: enrolmentSchema
              .pick({
                id: true,
                student: true,
                campus: true,
              })
              .extend({
                campus: campusSchema.pick({
                  id: true,
                  name: true,
                  province: true,
                }),
                student: studentSchema.pick({
                  id: true,
                  first_name: true,
                  last_name: true,
                }),
              }),
          }),
        )
        .catch([]),
      events_vehicles: z
        .array(
          z.object({
            vehicle: vehicleSchema.pick({
              id: true,
              name: true,
            }),
          }),
        )
        .optional()
        .catch([]),
      lesson: lessonSchema.pick({
        id: true,
        name: true,
      }),
      lesson_route: lessonRouteOptionSchema.nullish(),
      events_activities: z
        .array(
          z.object({
            id: z.string().uuid().optional(),
            duration: z.number().catch(0),
            activity: rowActivitiesTypeSchema.merge(
              z.object({
                activities_skills: z
                  .array(
                    z.object({
                      skill: rowSkillsTypeSchema,
                    }),
                  )
                  .optional()
                  .catch([]),
              }),
            ),
          }),
        )
        .optional()
        .catch([]),
      staff_events: z.array(
        z.object({
          instructor: staffSchema.pick({
            id: true,
            first_name: true,
            last_name: true,
          }),
        }),
      ),
      signed_by: staffSchema
        .pick({
          id: true,
          first_name: true,
          last_name: true,
        })
        .nullish(),
      location: locationSchema.pick({
        id: true,
        name: true,
        type: true,
      }),
    }),
  );

export type RawEventType = z.infer<typeof rawEventSchema>;

export const eventSchema = rowEventsTypeSchema
  .partial({ staff_signature: true })
  .merge(
    z.object({
      enrolments: z
        .array(
          enrolmentSchema
            .pick({
              id: true,
              student: true,
              course: true,
              campus: true,
            })
            .partial({
              course: true,
              campus: true,
            })
            .extend({
              student: studentSchema.pick({
                id: true,
                first_name: true,
                last_name: true,
              }),
            })
            .merge(
              z.object({
                enrolment_event_id: z.string().uuid().optional(),
                attendance_status: publicEventAttendanceStatusEnumSchema,
                is_optional_attendance: z.boolean().default(false).catch(false),
                student_signature: z.string().nullish(),
                student_sign_at: z.string().nullish(),
              }),
            ),
        )
        .catch([]),
      instructors: z.array(
        staffSchema
          .pick({
            id: true,
            first_name: true,
            last_name: true,
            email: true,
          })
          .partial({
            email: true,
          }),
      ),
      signed_by: staffSchema
        .pick({
          id: true,
          first_name: true,
          last_name: true,
        })
        .nullish(),
      activities: z.array(
        activitySchema
          .pick({
            id: true,
            name: true,
            type: true,
            duration: true,
            skills: true,
          })
          .merge(
            z.object({
              event_activity_id: z.string().uuid().optional(),
            }),
          ),
      ),
      lesson: lessonSchema
        .pick({
          id: true,
          name: true,
          module: true,
          is_vehicle_required: true,
          is_route_required: true,
        })
        .partial({
          is_vehicle_required: true,
          is_route_required: true,
        }),
      lesson_route: lessonRouteOptionSchema.nullish(),
      location: locationSchema
        .omit({
          created_at: true,
          updated_at: true,
          full_text_search: true,
          status: true,
          campuses: true,
        })
        .partial({
          address: true,
          url: true,
          province: true,
          lat: true,
          lon: true,
          place_id: true,
          geo_point: true,
        }),
      vehicles: z
        .array(
          vehicleSchema.pick({
            id: true,
            name: true,
          }),
        )
        .optional()
        .catch([]),
    }),
  );
export type EventType = z.infer<typeof eventSchema>;

export const updateEventFormSchema = updateEventsTypeSchema
  .omit({
    id: true,
    created_at: true,
    updated_at: true,
  })
  .merge(
    z.object({
      eventDate: z.string().optional(),
      eventTime: z.string().optional(),
      instructorIds: z.array(z.string()).optional().catch([]),
    }),
  );
export type UpdateEventFormType = z.infer<typeof updateEventFormSchema>;

export type EventInsertType = z.infer<typeof insertEventsTypeSchema>;

export type UpdateEventType = z.infer<typeof updateEventsTypeSchema>;

export const eventFormSchema = insertEventsTypeSchema
  .merge(
    z.object({
      isCustomTime: z.boolean().default(false),
      time: z.string().nonempty(),
      instructorIds: z.array(z.string()).default([]),
      enrolmentIds: z.array(z.string().uuid()).default([]),
    }),
  )
  .omit({
    id: true,
    created_at: true,
    updated_at: true,
  });
export type EventFormType = z.infer<typeof eventFormSchema>;

export type CreateEventCalendarContextType = {
  lesson: {
    id: string;
    name: string;
    module?: { id: string; name: string; color?: string };
  };
  location: { id: string; name: string; type?: EventType["location"]["type"] };
  instructors: { id: string; first_name: string; last_name: string }[];
  activities: { id: string; name: string; duration: number }[];
  enrolments: EventType["enrolments"];
};

export const attendanceRecordSchema = z.object({
  enrolmentId: z.string(),
  status: z.enum(["attending", "absent", ""]).default(""),
});

export type AttendanceRecordType = z.infer<typeof attendanceRecordSchema>;

export const enrolmentsEventsFormSchema = z
  .object({
    students: z.array(z.string()).default([]),
    observers: z.array(z.string()).default([]),
    attendanceRecords: z.array(attendanceRecordSchema).default([]),
  })
  .superRefine((data, ctx) => {
    // Check that no enrolment is both a student and an observer
    const duplicates = data.students.filter(id => data.observers.includes(id));

    if (duplicates.length > 0) {
      // Add an issue to both fields for better UX
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t("main:events.details.errors.duplicateStudents"),
        path: ["students"],
      });

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t("main:events.details.errors.duplicateStudents"),
        path: ["observers"],
      });
    }
  });

export type EnrolmentsEventsFormType = z.infer<
  typeof enrolmentsEventsFormSchema
>;

const eventFilterSchema = z.object({
  search: z.string().optional().catch(""),
  statuses: z.array(publicEventStatusEnumSchema).optional().catch([]),
  instructorIds: z.array(z.string()).optional().catch([]),
  campusesIds: z.array(z.string()).optional().catch([]),
  locationIds: z.array(z.string()).optional().catch([]),
  locationTypes: z.array(publicLocationTypeEnumSchema).optional(),
  lessonIds: z.array(z.string()).optional().catch([]),
  enrolmentIds: z.array(z.string()).optional().catch([]),
  isCancelledShown: z.boolean().default(false).optional().catch(false),
  dateRange: z
    .object({
      from: z.string().optional().catch(""),
      to: z.string().optional().catch(""),
    })
    .optional()
    .catch({ from: "", to: "" }),
});
export type EventFilterType = z.infer<typeof eventFilterSchema>;

export const calendarEventSearchParamsSchema = eventFilterSchema.extend({
  dateRange: z
    .object({
      from: z.string().optional().catch(""),
      to: z.string().optional().catch(""),
    })
    .optional()
    .default({
      from: dateAsDayjs().startOf("week").startOf("day").toISOString(),
      to: dateAsDayjs().endOf("week").endOf("day").toISOString(),
    })
    .catch({
      from: dateAsDayjs().startOf("week").startOf("day").toISOString(),
      to: dateAsDayjs().endOf("week").endOf("day").toISOString(),
    }),
  view: z
    .enum(["month", "week", "work_week", "day", "agenda"])
    .optional()
    .default("week")
    .catch("week"),
});

export type CalendarEventSearchParamsType = z.infer<
  typeof calendarEventSearchParamsSchema
>;

export const EventTabEnum = z.enum(["previous", "today", "upcoming"]);

export type EventTabType = z.infer<typeof EventTabEnum>;

export type EventByDateGroupType = {
  previous: EventType[];
  today: EventType[];
  upcoming: EventType[];
};

export const eventVehiclesFormSchema = z.object({
  vehicles: z.array(z.string()).default([]),
  odometerStart: z.preprocess(
    val => (val === "" ? null : Number(val)),
    z.number().nullable(),
  ),
  odometerEnd: z.preprocess(
    val => (val === "" ? null : Number(val)),
    z.number().nullable(),
  ),
});

export type EventVehiclesFormType = z.infer<typeof eventVehiclesFormSchema>;

export const eventVehicleSchema = vehicleSchema.pick({
  id: true,
  name: true,
});

export type EventVehicleType = z.infer<typeof eventVehicleSchema>;

export type CalendarRoutePathType = "/admin/calendars/" | "/admin/classrooms/";

export const eventStatusFormSchema = z.object({
  is_published: z.boolean().default(false),
});

export type EventStatusFormType = z.infer<typeof eventStatusFormSchema>;
